<div class="legal">
  <header class="legal__top">
    <div class="legal__bar">
      <a routerLink="/" class="legal-brand" aria-label="PulseWatch home">
        <span class="legal-brand__mark" aria-hidden="true">
          <svg viewBox="0 0 64 64"><path d="M9 35h11l5-12 8 24 6-17 3 5h13" /></svg>
        </span>
        <span class="legal-brand__name">PulseWatch</span>
      </a>
      <nav class="legal__nav" aria-label="Legal">
        <a routerLink="/imprint" routerLinkActive="is-active">Imprint</a>
        <a routerLink="/terms" routerLinkActive="is-active">Terms</a>
        <a routerLink="/privacy" routerLinkActive="is-active">Privacy</a>
      </nav>
    </div>
  </header>

  <main class="legal__main">
    @switch (page) {
      @case ('imprint') {
        <article class="legal-doc" data-testid="legal-imprint">
          <h1>Imprint</h1>
          <p class="legal-doc__lead">Information about the provider of PulseWatch.</p>

          <section>
            <h2>Provider</h2>
            <address>
              <strong>{{ company.name }}</strong><br />
              {{ company.street }}<br />
              {{ company.city }}<br />
              {{ company.country }}
            </address>
            <dl class="legal-facts">
              <dt>Represented by</dt><dd>{{ company.representative }}</dd>
              <dt>Email</dt><dd><a href="mailto:{{ company.email }}">{{ company.email }}</a></dd>
              <dt>Website</dt><dd><a routerLink="/">pulsewatch-app.facciobene.xyz</a></dd>
            </dl>
          </section>

          <section>
            <h2>Disclaimer</h2>
            <p>We take great care with the content of this website. Nevertheless, SM Tech GmbH assumes no warranty regarding the accuracy, timeliness, reliability and completeness of the information. We are not liable for material or immaterial damage resulting from access to, use or non-use of the information published here, from misuse of the connection or from technical faults.</p>
          </section>

          <section>
            <h2>Links to other websites</h2>
            <p>References and links to third-party websites are outside our area of responsibility. Access to and use of such websites is at the user’s own risk. We expressly declare that we have no influence on the design, content or offers of linked pages.</p>
          </section>

          <section>
            <h2>Copyright</h2>
            <p>The copyright and all other rights to the content, images, photos and other files on this website belong exclusively to SM Tech GmbH or to the rights holders named. Reproducing any of it requires the prior written consent of the rights holder.</p>
          </section>
        </article>
      }

      @case ('terms') {
        <article class="legal-doc" data-testid="legal-terms">
          <h1>Terms of Service</h1>
          <p class="legal-doc__lead">These terms govern the use of PulseWatch. Last updated: {{ updated }}.</p>

          <section>
            <h2>1. Scope</h2>
            <p>These terms apply to the use of the software platform PulseWatch and all related services provided by {{ company.name }}, {{ company.street }}, {{ company.city }} (“we”). You accept them when you register or use the platform. Terms of the customer that differ from these do not apply unless we agree to them in writing.</p>
          </section>

          <section>
            <h2>2. The service</h2>
            <p>PulseWatch is a cloud service (software as a service) for monitoring websites and applications: uptime checks, public status pages, error tracking, performance tracing and notifications. The software development kits (SDKs) and the API are part of the service. The features available depend on the plan you choose.</p>
          </section>

          <section>
            <h2>3. Account and organization</h2>
            <ul>
              <li>The information you give when registering must be true and kept up to date.</li>
              <li>Keep your password, API keys and ingest keys confidential. You are responsible for all activity in your organization, including by people you invite and by keys you create.</li>
              <li>The owner of an organization decides who is a member and which projects they can see.</li>
            </ul>
          </section>

          <section>
            <h2>4. Availability</h2>
            <p>We aim for an availability of 99.5% per calendar month, excluding announced maintenance. We announce planned maintenance in advance whenever possible. We may develop the service further and change or replace features, as long as the core purpose of the service is maintained.</p>
          </section>

          <section>
            <h2>5. Prices and payment</h2>
            <p>The plans and prices shown on our website apply. Prices are in Swiss francs (CHF) and exclude VAT. Paid plans are billed monthly or annually in advance; invoices are due within 30 days. If payment is late, we may restrict access to the service after a reminder. The free plan can be limited or discontinued with 30 days’ notice.</p>
          </section>

          <section>
            <h2>6. Term and cancellation</h2>
            <p>You can cancel your subscription at any time, effective at the end of the current billing period. After the end of the contract, you can still export your data for 90 days; after that we delete it, unless the law requires us to keep it. We may terminate the contract with 30 days’ notice, or immediately for good cause, in particular for a serious breach of these terms.</p>
          </section>

          <section>
            <h2>7. Acceptable use</h2>
            <p>You may only monitor systems you are entitled to monitor, and you must not use PulseWatch to attack, overload or probe third-party systems. You must not send data to PulseWatch that you are not allowed to process, and you should not send secrets (such as passwords or payment card numbers) in error reports or traces. We may suspend checks or accounts that endanger the service or third parties.</p>
          </section>

          <section>
            <h2>8. Your data and data processing on your behalf</h2>
            <p>Your content and the data your applications send us (for example error reports and traces, which can contain personal data of your users) remain yours. We process this data only on your behalf and according to your instructions, in order to provide the service, and we protect it with appropriate technical and organizational measures. You are responsible for having a legal basis for sending it to us and for informing your users. Details are in our <a routerLink="/privacy">privacy policy</a>; a data processing agreement is available on request.</p>
          </section>

          <section>
            <h2>9. Intellectual property</h2>
            <p>The software, including its code, design and documentation, belongs to {{ company.name }}. For the duration of your subscription you receive a non-exclusive, non-transferable right to use the service. The SDKs may be used under the licence stated with them.</p>
          </section>

          <section>
            <h2>10. Liability</h2>
            <p>We are liable without limitation for damage caused intentionally or by gross negligence. Otherwise, to the extent permitted by law, liability for indirect damage, consequential damage and lost profit is excluded, and our liability is limited to the fees you paid in the 12 months before the damage occurred. PulseWatch reports on your systems but does not guarantee that every outage or error is detected or notified. We are not liable for force majeure.</p>
          </section>

          <section>
            <h2>11. Changes to these terms</h2>
            <p>We may change these terms. We announce material changes at least 30 days in advance by email. If you keep using PulseWatch after the change takes effect, you accept the new terms; if you don’t agree, you can cancel before then.</p>
          </section>

          <section>
            <h2>12. Governing law and jurisdiction</h2>
            <p>These terms are governed by Swiss law, excluding the UN Convention on Contracts for the International Sale of Goods. The place of jurisdiction is Zurich, Switzerland. Mandatory consumer protection rules of the consumer’s place of residence remain reserved.</p>
          </section>
        </article>
      }

      @case ('privacy') {
        <article class="legal-doc" data-testid="legal-privacy">
          <h1>Privacy Policy</h1>
          <p class="legal-doc__lead">How we handle personal data when you visit our website and use PulseWatch. Last updated: {{ updated }}.</p>

          <section>
            <h2>1. Controller</h2>
            <address>
              <strong>{{ company.name }}</strong><br />
              {{ company.street }}<br />
              {{ company.city }}, {{ company.country }}<br />
              <a href="mailto:{{ company.email }}">{{ company.email }}</a>
            </address>
            <p>We process personal data in accordance with the Swiss Federal Act on Data Protection (FADP/DSG) and, where it applies, the EU General Data Protection Regulation (GDPR).</p>
          </section>

          <section>
            <h2>2. Data we process</h2>
            <h3>Visiting our website</h3>
            <p>When you open our website, our servers process technical data such as your IP address, date and time, the pages requested and your browser’s user agent, to deliver the website and to keep it secure.</p>
            <h3>Your account</h3>
            <p>When you register or accept an invitation, we process your name, email address, optional phone number, your organization and your role in it, and your login activity. We use this data to provide the service (contract performance).</p>
            <h3>Monitoring data</h3>
            <ul>
              <li><strong>Uptime checks:</strong> the addresses you monitor and the results of the checks.</li>
              <li><strong>Error reports and traces</strong> sent by your applications through our SDKs or API. They can contain personal data of your users, for example IP addresses, user IDs or email addresses you attach. For this data we act as a processor on behalf of our customer.</li>
              <li><strong>Public status pages:</strong> when someone views a status page, we record the view with IP address and user agent to show visit statistics to the page’s owner.</li>
            </ul>
            <h3>Contacting us and the newsletter</h3>
            <p>If you request access to an organization, subscribe to our newsletter or contact us, we process the data you give us (name, email, message) to handle your request. You can unsubscribe from the newsletter at any time.</p>
          </section>

          <section>
            <h2>3. Where your data is stored</h2>
            <p>PulseWatch runs on Google Cloud in the region europe-west1 (Belgium, EU). Uploaded files such as project logos are stored in Google Cloud Storage. Data is encrypted in transit (TLS).</p>
          </section>

          <section>
            <h2>4. Service providers</h2>
            <p>We use the following providers, which process data on our behalf:</p>
            <ul>
              <li><strong>Google Cloud</strong> (Google Ireland Ltd.): hosting, database and file storage.</li>
              <li><strong>Zoho</strong> (Zoho Corporation B.V., EU data centre): sending emails such as invitations, password resets and alerts.</li>
              <li><strong>Sentry</strong> (Functional Software Inc., USA): reports of errors in PulseWatch itself, which can contain technical data and the email address of the affected user.</li>
              <li><strong>Google Analytics / Firebase</strong> (Google): statistics about the use of our website and web app.</li>
              <li><strong>Google Fonts</strong> and <strong>jsDelivr</strong>: fonts and style sheets, loaded from their servers; your IP address is transmitted to them.</li>
              <li><strong>Telegram</strong>: only if you link a Telegram chat for alerts; alerts are then sent through Telegram.</li>
            </ul>
            <p>Some of these providers are located outside Switzerland and the EU, in particular in the USA. Where no adequate level of data protection is guaranteed, we rely on standard contractual clauses or other appropriate safeguards.</p>
          </section>

          <section>
            <h2>5. Cookies and local storage</h2>
            <p>We use your browser’s local storage to keep you logged in. Google Analytics sets cookies to distinguish visits. You can block or delete cookies in your browser settings; the app still works without analytics cookies.</p>
          </section>

          <section>
            <h2>6. How long we keep data</h2>
            <ul>
              <li>Error events: 30 days. Traces: 7 days.</li>
              <li>Account and organization data: as long as your account exists; after cancellation you can export it for 90 days, then we delete it.</li>
              <li>Data we must keep by law (for example invoices): as long as the law requires, usually 10 years.</li>
            </ul>
          </section>

          <section>
            <h2>7. Your rights</h2>
            <p>You have the right to information about your personal data and to have it corrected or deleted, and, under the GDPR, the right to restriction of processing, data portability and to object to processing. To exercise these rights, write to <a href="mailto:{{ company.email }}">{{ company.email }}</a>. If your data was sent to us by one of our customers (for example in an error report), please contact that customer first; we support them in answering your request.</p>
            <p>You can lodge a complaint with a supervisory authority, in Switzerland the Federal Data Protection and Information Commissioner (FDPIC).</p>
          </section>

          <section>
            <h2>8. Changes</h2>
            <p>We may update this privacy policy when our service or the law changes. The version published here applies.</p>
          </section>
        </article>
      }
    }
  </main>

  <footer class="legal__footer">
    <p>&#x1F1E8;&#x1F1ED; Swiss made software · © {{ year }} {{ company.name }}</p>
  </footer>
</div>
